/**
 * ProductRecommendationFlow — 2-phase assessment driven by LK_ProgramMapper / AI_App_Context.
 *
 * Phase 1: router essentials (purpose, refi goal, occupancy, value, payoff/down, credit, loan-size tier).
 * Phase 2: gates (HFA, tribal, USDA, agency refi specials, renovation, doc path) + term/rate prefs.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '../../ui/components';
import { Sparkles, SlidersHorizontal, Star, CheckCircle, Home, Info, Loader2 } from 'lucide-react';
import {
  updateProductRecommendation,
  setRecommendedProduct,
  setRecommendedProducts,
  setCurrentStep,
  setUser,
} from '../../store/loanApplicationSlice';
import { calculateRecommendations } from '../../utils/recommendationEngine';
import {
  RECOMMENDATION_QUESTIONS,
  getQuestionText,
  getQuestionSubtitle,
  getQuestionPlaceholder,
} from '../../config/recommendationQuestions';
import { calculateLivePayment, getEstimatedRate, monthlyPI, formatRate } from '../../utils/mortgageCalculations';
import ApplyLayout from '../layout/ApplyLayout';
import { RadioCardGroup, CurrencyInput, FormInput } from '../forms';
import AccountCreationModal from '../Auth/AccountCreationModal';
import LivePaymentEstimator from './LivePaymentEstimator';
import BuyingPowerCard from './BuyingPowerCard';
import LiveReadinessScore from './LiveReadinessScore';
import ContextualEducation from './ContextualEducation';
import ReadinessBreakdown from './ReadinessBreakdown';
import OwenInlineSummary, { OwenBuyingPowerSummary, OwenRecommendationsSummary } from './OwenInlineSummary';
import AssessmentQuestionPanel from './AssessmentQuestionPanel';
import ForwardPacketCard from './ForwardPacketCard';
import V1ProductFlow from './v1/V1ProductFlow';
import { LK_FEATURES } from '../../config/features';
import ConversationalApplyFlow from '../apply/ConversationalApplyFlow';

// ── Phase definitions ──────────────────────────────────────────────
// Phase 1: router essentials (must match hasCore in useEffect below)
const PHASE_1_IDS = [
  'purchaseOrRefinance',
  'refiGoal',
  'propertyUse',
  'estimatedValue',
  'downPayment',
  'creditScore',
  'loanSizeTier',
];

// Phase 2: everything else (optional refinement)
const isPhase1Question = (q) => PHASE_1_IDS.includes(q.id);

const LegacyProductRecommendationFlow = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const productRecommendation = useSelector((state) => state.loanApplication.productRecommendation);
  const recommendations = useSelector((state) => state.loanApplication.recommendedProducts);
  const isAuthenticated = useSelector((state) => state.loanApplication.isAuthenticated);

  // ── Local state ────────────────────────────────────────────────
  const [questionIndex, setQuestionIndex] = useState(0);
  const [phase, setPhase] = useState(1);              // 1 = core, 'buyingPower' = interstitial, 2 = refinement, 'results' = final
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  // ── Question lists ─────────────────────────────────────────────
  const allVisible = useMemo(() => {
    return RECOMMENDATION_QUESTIONS.filter(q => {
      if (!q.showIf) return true;
      return q.showIf(productRecommendation);
    });
  }, [productRecommendation]);

  const phase1Questions = useMemo(() => allVisible.filter(isPhase1Question), [allVisible]);
  const phase2Questions = useMemo(() => allVisible.filter(q => !isPhase1Question(q)), [allVisible]);

  const questions = phase === 1 ? phase1Questions : phase2Questions;
  const currentQuestion = questions[questionIndex];
  const currentValue = productRecommendation[currentQuestion?.id] || '';
  const isLastInPhase = questionIndex === questions.length - 1;
  const canContinue = currentValue !== '';

  // ── Compute recommendations whenever answers change ────────────
  useEffect(() => {
    const pr = productRecommendation;
    const hasCore =
      pr.purchaseOrRefinance &&
      pr.propertyUse &&
      pr.estimatedValue &&
      pr.downPayment &&
      pr.creditScore &&
      pr.loanSizeTier &&
      (pr.purchaseOrRefinance === 'purchase' || pr.refiGoal);
    if (hasCore) {
      const programs = calculateRecommendations(productRecommendation);
      dispatch(setRecommendedProducts(programs));
    }
  }, [productRecommendation, dispatch]);

  // ── Handlers ───────────────────────────────────────────────────
  const handleAnswer = (value) => {
    dispatch(updateProductRecommendation({ [currentQuestion.id]: value }));
  };

  const handleNext = () => {
    if (phase === 1 && isLastInPhase) {
      // Phase 1 complete → show Buying Power interstitial
      setPhase('buyingPower');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (phase === 2 && isLastInPhase) {
      // Phase 2 complete → show final recommendations
      setPhase('results');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setQuestionIndex(questionIndex + 1);
    }
  };

  const handleBack = () => {
    if (questionIndex > 0) {
      setQuestionIndex(questionIndex - 1);
    } else if (phase === 2) {
      // Go back to buying power interstitial
      setPhase('buyingPower');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSkipToResults = () => {
    setPhase('results');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRefineMore = () => {
    setPhase(2);
    setQuestionIndex(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectProduct = (product) => {
    setSelectedProductId(product.id);
    dispatch(setRecommendedProduct(product));

    if (isAuthenticated) {
      setTimeout(() => {
        dispatch(setCurrentStep(1));
        setSelectedProductId(null);
      }, 100);
    } else {
      setShowAccountModal(true);
    }
  };

  const handleAccountCreated = (userData) => {
    dispatch(setUser({
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      authMethod: 'demo',
    }));
    setTimeout(() => {
      dispatch(setCurrentStep(1));
    }, 100);
  };

  // ── Estimated payment for recommendation cards ─────────────────
  const getProductPayment = (product) => {
    const homeValue = parseFloat(productRecommendation.estimatedValue) || 0;
    const dp = parseFloat(productRecommendation.downPayment) || 0;
    const loanAmt =
      productRecommendation.purchaseOrRefinance === 'refinance'
        ? dp
        : Math.max(0, homeValue - dp);
    if (loanAmt <= 0) return null;
    const rate = getEstimatedRate(productRecommendation.creditScore, product.fixedPeriod);
    const pi = monthlyPI(loanAmt, rate, product.amortization || product.fixedPeriod);
    return { payment: Math.round(pi), rate };
  };

  // ══════════════════════════════════════════════════════════════
  // BUYING POWER INTERSTITIAL (between Phase 1 and Phase 2)
  // ══════════════════════════════════════════════════════════════
  if (phase === 'buyingPower') {
    const isRefi = productRecommendation.purchaseOrRefinance === 'refinance';
    return (
      <ApplyLayout
        step={phase1Questions.length}
        totalSteps={phase1Questions.length + phase2Questions.length}
        title={isRefi ? 'Your Refinance Snapshot' : 'Your Buying Power'}
        subtitle={isRefi
          ? 'Here\'s what your new loan looks like based on your answers so far'
          : 'Here\'s what you can afford based on your answers so far'}
        context="buyingPower"
        showProgress={false}
        rightRailContent={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
            <ReadinessBreakdown compact />
            <LivePaymentEstimator />
          </div>
        }
      >
        {/* Owen buying power insight */}
        <OwenBuyingPowerSummary answers={productRecommendation} />

        <BuyingPowerCard />

        {/* Readiness Breakdown — full explainability */}
        <div style={{ marginTop: 'var(--lk-space-8)' }}>
          <ReadinessBreakdown />
        </div>

        {/* Two CTAs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)', maxWidth: 720, margin: 'var(--lk-space-6) auto 0' }}>
          <Button variant="primary" size="lg" icon={Sparkles} onClick={handleSkipToResults} style={{ width: '100%' }}>
            See My Recommended Loan Programs
          </Button>

          <Button variant="outline" size="lg" icon={SlidersHorizontal} onClick={handleRefineMore} style={{ width: '100%' }}>
            Answer More Questions for Better Matches
            <span style={{ marginLeft: 8, padding: '2px 10px', borderRadius: 12, backgroundColor: 'var(--lk-primary-50)', color: 'var(--lk-primary-600)', fontSize: 11, fontWeight: 600 }}>
              {phase2Questions.length} questions
            </span>
          </Button>
        </div>

        <p style={{ fontSize: 12, color: 'var(--lk-text-quaternary)', textAlign: 'center', marginTop: 'var(--lk-space-4)' }}>
          You can always refine later. We already have enough to show you great options.
        </p>
      </ApplyLayout>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // RECOMMENDATIONS VIEW (final results)
  // ══════════════════════════════════════════════════════════════
  if (phase === 'results') {
    return (
      <>
        <AccountCreationModal
          open={showAccountModal}
          onClose={() => {
            setShowAccountModal(false);
            setSelectedProductId(null);
          }}
          onAccountCreated={handleAccountCreated}
          savedData={productRecommendation}
        />

        <ApplyLayout
          step={0}
          totalSteps={9}
          title="Your Personalized Recommendations"
          subtitle={`Program router matched ${recommendations.length} SKU${recommendations.length !== 1 ? 's' : ''} from your answers (sorted by workbook routePriority)`}
          context="recommendations"
          showProgress={false}
          rightRailContent={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
              <ReadinessBreakdown compact />
              <LivePaymentEstimator />
            </div>
          }
        >
          {/* Owen recommendations insight */}
          <OwenRecommendationsSummary recommendations={recommendations} answers={productRecommendation} />

          {/* Refine prompt */}
          {phase2Questions.some(q => !productRecommendation[q.id]) && (
            <div style={{
              padding: 'var(--lk-space-4) var(--lk-space-5)',
              backgroundColor: 'var(--lk-warning-50)', borderRadius: 'var(--lk-radius-lg)',
              border: '1px solid var(--lk-warning-200)', marginBottom: 'var(--lk-space-6)',
              fontSize: 'var(--lk-text-sm)', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Info size={16} color="var(--lk-warning-600)" />
                <span><strong>Want better matches?</strong> Answer a few more questions to unlock specialized programs.</span>
              </div>
              <Button variant="secondary" size="sm" icon={SlidersHorizontal} onClick={handleRefineMore}>
                Refine
              </Button>
            </div>
          )}

          {/* Recommendations List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-5)' }}>
            {recommendations.map((product, index) => {
              const paymentInfo = getProductPayment(product);
              const featureRow = (text) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
                  <CheckCircle size={16} color="var(--lk-success-500)" />
                  <span style={{ fontSize: 'var(--lk-text-base)' }}>{text}</span>
                </div>
              );
              return (
                <div
                  key={product.id}
                  style={{
                    backgroundColor: index === 0 ? 'var(--lk-primary-50)' : 'var(--lk-bg-surface-1)',
                    border: index === 0 ? '2px solid var(--lk-primary-500)' : '1px solid var(--lk-border)',
                    borderRadius: 'var(--lk-radius-xl)', padding: 'var(--lk-space-8)',
                    position: 'relative',
                  }}
                >
                  {index === 0 && (
                    <div style={{
                      position: 'absolute', top: -12, right: 24,
                      backgroundColor: 'var(--lk-primary-500)', color: 'white',
                      padding: '6px 16px', borderRadius: 20,
                      fontSize: 'var(--lk-text-xs)', fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      <Star size={14} /> Best Match
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--lk-space-3)', marginBottom: 'var(--lk-space-3)' }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 'var(--lk-radius-md)',
                      backgroundColor: 'var(--lk-primary-50)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Home size={24} color="var(--lk-primary-500)" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>
                        {product.displayName || product.name}
                      </h3>
                      <p style={{ color: 'var(--lk-text-tertiary)', marginBottom: 8 }}>
                        {product.productFamily} · {product.rateType} · {product.fixedPeriod} yr
                        {product.rateType === 'ARM' && product.armIndex ? ` · ${product.armIndex}` : ''}
                      </p>
                      <p style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)', marginBottom: 0 }}>
                        Route priority {product.routePriority}
                        {product.tieBreakGroup ? ` · ${product.tieBreakGroup}` : ''}
                      </p>
                    </div>
                    {paymentInfo && (
                      <div style={{
                        textAlign: 'center', minWidth: 140,
                        backgroundColor: index === 0 ? 'var(--lk-primary-100)' : 'var(--lk-primary-50)',
                        borderRadius: 'var(--lk-radius-lg)', padding: '12px 16px',
                      }}>
                        <div style={{ fontSize: 11, color: 'var(--lk-text-tertiary)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                          Est. P&I Payment
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--lk-primary-600)' }}>
                          ${paymentInfo.payment.toLocaleString()}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--lk-text-tertiary)' }}>at {formatRate(paymentInfo.rate)}</div>
                      </div>
                    )}
                  </div>

                  {/* Rationale */}
                  {product.rationale && product.rationale.length > 0 && (
                    <div style={{ marginBottom: 'var(--lk-space-5)' }}>
                      <div style={{ fontSize: 'var(--lk-text-xs)', fontWeight: 600, color: 'var(--lk-text-secondary)', marginBottom: 8 }}>
                        Why we recommend this:
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {product.rationale.map((reason, idx) => (
                          <li key={idx} style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)', marginBottom: 4 }}>
                            {reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div style={{ borderTop: '1px solid var(--lk-border-subtle)', paddingTop: 'var(--lk-space-5)', paddingLeft: 52 }}>
                    <div style={{ fontSize: 'var(--lk-text-xs)', fontWeight: 600, color: 'var(--lk-text-tertiary)', marginBottom: 12 }}>
                      KEY FEATURES
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)' }}>
                      {product.qualifyingMethod && featureRow(product.qualifyingMethod)}
                      {featureRow(`Typical min credit (estimate): ${product.minCreditScore}`)}
                      {featureRow(`Min down payment (estimate): ${product.minDownPayment}%`)}
                      {featureRow(`Max LTV (estimate): ${product.maxLTV}%`)}
                      {product.investor && featureRow(`Investor / channel: ${product.investor}`)}
                      {product.renovationProgram && featureRow('Renovation SKU')}
                      {product.requiresMilitary && featureRow('Veteran eligibility required')}
                      {product.requiresRural && featureRow('USDA-eligible area')}
                    </div>
                  </div>

                  <Button
                    variant={index === 0 ? 'primary' : 'outline'}
                    size="lg"
                    onClick={() => handleSelectProduct(product)}
                    disabled={selectedProductId === product.id}
                    style={{ width: '100%', marginTop: 'var(--lk-space-5)' }}
                  >
                    {selectedProductId === product.id ? (
                      <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite', marginRight: 8 }} /> Processing...</>
                    ) : (
                      <>Continue with {product.displayName || product.name}</>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Forward Packet — primary MVP exit ramp */}
          <ForwardPacketCard packetVersion="recommendation" />

          {/* Full Readiness Breakdown */}
          <div style={{ marginTop: 'var(--lk-space-8)' }}>
            <ReadinessBreakdown defaultExpanded />
          </div>
        </ApplyLayout>
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // QUESTIONS VIEW (Phase 1 or Phase 2)
  // ══════════════════════════════════════════════════════════════
  const renderInput = () => {
    const { type, options, placeholder } = currentQuestion;
    const purpose = productRecommendation.purchaseOrRefinance;
    const ph = getQuestionPlaceholder(currentQuestion, purpose) || placeholder;

    if (type === 'radio' && options) {
      return (
        <RadioCardGroup
          emphasized
          value={currentValue}
          onChange={(e) => handleAnswer(e.target.value)}
          options={options.map(opt => ({
            value: opt.value,
            label: opt.label,
            iconClass: opt.iconClass,
            description: opt.description,
          }))}
        />
      );
    }

    if (type === 'currency') {
      return (
        <CurrencyInput
          value={currentValue}
          onChange={(e) => handleAnswer(e.target.value)}
          placeholder={ph}
        />
      );
    }

    return (
      <FormInput
        type={type}
        value={currentValue}
        onChange={(e) => handleAnswer(e.target.value)}
        placeholder={ph}
      />
    );
  };

  // Phase label for progress
  const phaseLabel = phase === 1 ? 'Quick Assessment' : 'Refine Your Match';
  const totalStepsInPhase = questions.length;
  const stepInPhase = questionIndex + 1;

  // Right rail: readiness score + contextual education + live estimator (when data available)
  const hasEstimateData = productRecommendation.estimatedValue && productRecommendation.creditScore;

  const loanPurpose = productRecommendation.purchaseOrRefinance;
  const questionTitle = currentQuestion ? getQuestionText(currentQuestion, loanPurpose) : '';
  const questionSubtitle = currentQuestion ? getQuestionSubtitle(currentQuestion, loanPurpose) : '';

  const questionRightRail = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
      <LiveReadinessScore compact />
      <ContextualEducation question={currentQuestion} defaultCollapsed />
      {hasEstimateData && <LivePaymentEstimator />}
    </div>
  );

  return (
    <>
      <AccountCreationModal
        open={showAccountModal}
        onClose={() => setShowAccountModal(false)}
        onAccountCreated={handleAccountCreated}
        savedData={productRecommendation}
      />

      <ApplyLayout
        step={stepInPhase}
        totalSteps={totalStepsInPhase}
        title={phaseLabel}
        subtitle="Answer each question in the highlighted box below."
        contentMaxWidth="full"
        context="assessment"
        rightRailContent={questionRightRail}
      >
        <AssessmentQuestionPanel
          stepNumber={stepInPhase}
          totalSteps={totalStepsInPhase}
          phaseLabel={phase === 2 ? `Refining — ${phase2Questions.length - questionIndex} left` : phaseLabel}
          question={questionTitle}
          subtitle={questionSubtitle}
          answerType={currentQuestion?.type || 'radio'}
          coachContent={<OwenInlineSummary questionId={currentQuestion?.id} phase={phase} />}
          footer={(
            <>
              <div style={{ display: 'flex', gap: 'var(--lk-space-3)' }}>
                {(questionIndex > 0 || phase === 2) && (
                  <Button variant="outline" size="lg" onClick={handleBack} style={{ minWidth: 120 }}>
                    Back
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleNext}
                  disabled={!canContinue}
                  style={{ flex: 1 }}
                >
                  {phase === 1 && isLastInPhase
                    ? (loanPurpose === 'refinance' ? 'See My Refinance Snapshot' : 'See My Buying Power')
                    : phase === 2 && isLastInPhase
                      ? 'See My Recommendations'
                      : 'Continue'}
                </Button>
              </div>
              {phase === 2 && questionIndex > 0 && (
                <div style={{ textAlign: 'center', marginTop: 'var(--lk-space-3)' }}>
                  <button
                    type="button"
                    onClick={handleSkipToResults}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)',
                      padding: 'var(--lk-space-2)',
                    }}
                  >
                    Skip remaining questions →
                  </button>
                </div>
              )}
            </>
          )}
        >
          {renderInput()}
        </AssessmentQuestionPanel>
      </ApplyLayout>
    </>
  );
};

const ProductRecommendationFlow = () => <ConversationalApplyFlow />;

export default ProductRecommendationFlow;
